import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Zoom,
} from "@mui/material";
import { Colors } from "../../../../configs";
import {
  CartItem,
  IDamage,
  IInstruction,
} from "../../../../typings/interfaces/pos/cart-item";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import { cloneDeep, startCase, upperFirst } from "lodash";
import CrossIcon from "../../../../assets/icons/cross-icon";
import {
  getCashierDraftsAction,
  updateCartSessionItem,
} from "../../../../redux/actions/cartAction";
import { CustomTextBox } from "../../../atoms";
import GarmentColor from "../../../../typings/enum/color";
import { ISelectedUpcharge } from "../../../../typings/interfaces/pos/upCharge";
import { ISelectedSpot } from "../../../../typings/interfaces/pos/spot";
import { formatCurrency } from "../../../../util/common-functions";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import ItemNoteModal from "../../../pos/notes-modal/itemNoteModal";
import { getFilteredBillsAction } from "../../../../redux/actions/pintagAction";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { getAssignedConveyorsAction } from "../../../../redux/actions/authAction";
import ManagerCodeModal from "../../manager-code-modal/ManagerCodeModal";
import OrderCartItemProcessType from "./OrderCartItemProcessType";
import { BillPickupStatus } from "../../../../typings/enum/paymentTypes";
import { primaryAlpha } from "../../../../configs/colors";
import { sessionSpecialCharge } from "../../../../util/special-charge";

export interface ICartRowProps {
  // data: {
  // qty: number;
  // serviceType: string;
  // textureType: string;
  // color: GarmentColor[];
  // clothSubType: string;
  // materialType: string;
  // clothType: string;
  // instructions: IInstruction[];
  // damages: IDamage[];
  // upcharges: ISelectedUpcharge[];
  // unitPrice: number;
  // };
  status: "In Progress" | "Completed" | "Cancelled";
  firstRow: boolean;
  lastRow: boolean;
  onSelect(): void;
  cartItem: CartItem;
  alterationUnitPrice?: number;
  mode?: "checkin" | "pickup";
}

const CartRow: FC<ICartRowProps> = ({
  alterationUnitPrice,
  status,
  firstRow,
  lastRow,
  cartItem,
  mode = "checkin",
}) => {
  const [unitPrice, setUnitPrice] = useState<number>(
    Number(cartItem.unitPrice.toFixed(2))
  );
  const [alterationUnitPriceState, setAlterationUnitPriceState] = useState<
    number | undefined
  >(Number(alterationUnitPrice?.toFixed(2)));
  const [itemNoteModalOpen, setItemNoteModalOpen] = useState<boolean>(false);
  const { cartHistorySelectedRows, billById, billItemIdList } = useAppSelector(
    (state) => state.cart
  );
  const dispatch = useAppDispatch();

  // Edit Invoice reuses this row in "checkin" mode against an already-saved
  // bill (billById) - an item already handed to the customer in a prior
  // partial pickup, OR already paid for via an item-scoped payment (even if
  // not yet picked up - e.g. a plain "Collect Payment"), must not be
  // re-editable here. billItemIdList only ever has an entry for billById.id
  // when the edit-invoice page explicitly fetched it (see PosDashboardPage's
  // fetchBillId), so a brand-new/duplicated session (fresh itemIds, no
  // matching entry) is never affected.
  const lockedItemRecord = useMemo(() => {
    const entry = billItemIdList.find((b) => b.billId === billById?.id);
    return entry?.billItemIdList.find((item) => item.hashId === cartItem.itemId);
  }, [billItemIdList, billById?.id, cartItem.itemId]);

  // Picked-up items get struck through (physically gone, the description no
  // longer applies); paid-but-not-yet-picked-up items are disabled the same
  // way (can't be re-edited) but keep their normal text - strikethrough on a
  // still-in-store item read as "picked up" at a glance, which is what
  // caused this to be split into two flags instead of one. Named
  // `isItemPickedUp` (not `isPickedUp`) to avoid colliding with the
  // bill-wide `isPickedUp` derived from `pickingStatus` further below - a
  // different, bill-level concept from this item-level one.
  const isItemPickedUp =
    lockedItemRecord?.pickupStatus === BillPickupStatus.PICKED_UP;
  const isItemLocked = isItemPickedUp || lockedItemRecord?.isPaid === true;

  const lockedLabel = isItemPickedUp ? "(Picked Up)" : "(Paid)";

  const { filteredBills } = useSelector((state: RootState) => state.pintag);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(1500);

  const bills = [...(filteredBills?.body?.content || [])];

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getFilteredBillsAction({ page, size }));
      } catch (error) {
        console.error("Error fetching data", error);
      }
    };

    fetchData();
  }, [dispatch, page, size]);

  useEffect(() => {
    dispatch(getAssignedConveyorsAction({ page: 0, size: 1000 }));
  }, [dispatch]);

  // A Special ticket's extra charge for this item: it counts in the order
  // summary, so it gets its own line on the row
  const cartSessions = useAppSelector((state) => state.cart.cart);
  const specialChargeRules = useAppSelector((state) => state.specialCharge.configs);
  const itemSession = cartSessions.find((session) =>
    session.cartItems?.some((item) => item.itemId === cartItem.itemId)
  );
  const specialCharge =
    mode === "checkin" && itemSession
      ? sessionSpecialCharge(cartItem, itemSession.isSpecial, specialChargeRules)
      : 0;

  const selectedRowIndex = cartHistorySelectedRows.length - 1;
  const { selectedCartItem, cartMode } = useAppSelector((state) => state.cart);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  // On the selected row the prices are open to typing. A changed price waits
  // here (the item's unit price, or an alteration's index) for the manager
  // code before it reaches the cart.
  const pendingPriceRef = useRef<"base" | number | null>(null);
  const [alterationDrafts, setAlterationDrafts] = useState<Record<number, number>>({});
  const priceInputName = (target: "base" | number) =>
    target === "base"
      ? `unit-price-${cartItem.itemId}`
      : `alteration-price-${cartItem.itemId}-${target}`;
  const {
    assignedConveyors,
    assignedConveyorsLoading,
    assignedConveyorsError,
    user,
  } = useAppSelector((state) => state.auth);

  const selectedItems = Array.isArray(selectedCartItem) ? selectedCartItem : [];

  // voidStatus
  const voidStatus = filteredBills?.body?.content?.find((bill: any) =>
    selectedItems.length > 0
      ? bill.billId === cartHistorySelectedRows[selectedRowIndex]?.billId
      : bill.billId === billById?.billId
  )?.isVoided;

  // refundStatus
  const refundStatus = filteredBills?.body?.content?.find((bill: any) =>
    selectedItems.length > 0
      ? bill.billId === cartHistorySelectedRows[selectedRowIndex]?.billId
      : bill.billId === billById?.billId
  )?.isRefunded;

  const selectedRow =
    cartHistorySelectedRows?.[cartHistorySelectedRows.length - 1];
  const targetBillId =
    Array.isArray(selectedCartItem) && selectedCartItem.length > 0
      ? selectedRow?.id
      : billById?.id;

  const conveyors = assignedConveyors ?? assignedConveyors ?? [];

  const conveyorMatch =
    conveyors.find((b: any) => String(b.billId) === String(targetBillId)) ??
    null;

  const hasConveyor = Boolean(
    targetBillId &&
    conveyors.some((b: any) => String(b.billId) === String(targetBillId))
  );

  const pickingStatus =
    filteredBills?.body?.content?.find((bill: any) =>
      selectedItems.length > 0
        ? bill.billId === cartHistorySelectedRows[selectedRowIndex]?.billId
        : bill.billId === billById?.billId
    )?.pickingFilterStatus == "PICKED_UP";

  useEffect(() => {
    setUnitPrice(cartItem.unitPrice);
  }, [cartItem.unitPrice]);

  useEffect(() => {
    setAlterationUnitPriceState(alterationUnitPrice);
  }, [alterationUnitPrice]);

  const handleOnSelectCartItem = () => {
    if (selectedCartItem?.itemId === cartItem.itemId) {
      dispatch(CartSliceAction.setSelectedCartItem(null));
      return;
    }
    dispatch(CartSliceAction.setSelectedCartItem(cartItem));
  };

  const handleManagerAuthSuccess = () => {
    setCodeModalOpen(false);
    applyPendingPrice();
  };

  const dcSelectionText = [
    cartItem?.garmentType?.name || "",
    cartItem?.garmentStyle?.name || "",
    startCase((cartItem.materialType?.name || "").toLowerCase()),
  ];

  // const instructions = cartItem.instructions.map((item) =>
  //   upperFirst(item.label.toLowerCase())
  // );
  // const damages = cartItem.damages.map((item) =>
  //   upperFirst(item.label.toLowerCase())
  // );

  const isSelected = selectedCartItem?.itemId === cartItem.itemId;

  const styles = {
    tableBodyCell: {
      background: isItemLocked
        ? "#F8F8F8"
        : isSelected
        ? primaryAlpha(0.09)
        : "#F4F5F7",
      opacity: isItemLocked ? 0.6 : 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "1.07rem 1.28rem",
      paddingBottom: "0rem",
      boxSizing: "border-box",
      gap: "1.07rem",
      transition: "background 0.15s ease",
    },
    descriptionCell: {
      alignItems: "flex-start",
    },
    firstRowCell: {
      borderTopLeftRadius: "0.85rem",
      borderTopRightRadius: "0.85rem",
    },
    lastRowCell: {
      borderBottomRightRadius: "0.85rem",
      borderBottomLeftRadius: "0.85rem",
      paddingBottom: "1.07rem",
    },
    tableRow: {},
  };

  const getPrice = (mode: "alteration" | "other" = "other") => {
    if (cartItem?.redoDetails || cartItem?.alterationOnly) {
      return formatCurrency({ amount: 0 });
    }
    if (
      (cartItem?.serviceType?.categoryName === "Alteration" &&
        alterationUnitPriceState) ||
      mode === "alteration"
    ) {
      return formatCurrency({
        amount: cartItem.qty * (alterationUnitPriceState || 0),
      });
      // cartItem.qty * (alterationUnitPrice || 0)
      //   ? (cartItem.qty * (alterationUnitPrice || 0)).toFixed(2).toLocaleString()
      //   : "N/A";
    } else {
      return formatCurrency({ amount: cartItem.qty * unitPrice });
      // cartItem.qty * unitPrice
      //   ? (cartItem.qty * unitPrice).toFixed(2).toLocaleString()
      //   : "N/A";
    }
  };

  // Press Only / Clean Only is its own, cheaper service: the regular price is
  // shown struck through and the Press/Clean Only line carries the price.
  const processingBasePrice =
    (cartItem.pressOnly || cartItem.cleanOnly) &&
    !cartItem.isPriceEdited &&
    cartItem.baseUnitPrice &&
    cartItem.baseUnitPrice > unitPrice
      ? cartItem.baseUnitPrice
      : null;

  const handleUnitPriceChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(event.target.value);
    setUnitPrice(isNaN(newValue) ? 0 : newValue);
  };

  const getUnitPrice = (mode: "alteration" | "other" = "other") => {
    if (cartItem?.redoDetails || cartItem?.alterationOnly) {
      return formatCurrency({ amount: 0 });
    }
    if (
      (cartItem?.serviceType?.categoryName === "Alteration" &&
        alterationUnitPriceState) ||
      mode === "alteration"
    ) {
      return formatCurrency({ amount: alterationUnitPriceState ?? 0 });
    } else {
      return formatCurrency({ amount: unitPrice ?? 0 });
    }
  };

  /** Whether this price can be changed at all */
  const priceIsEditable = (target: "base" | number) =>
    !cartItem?.redoDetails &&
    !isItemLocked &&
    !(target === "base" && cartItem?.alterationOnly);

  // In pickup mode rows can't be selected, so a tapped price opens on its own
  const [tappedPrice, setTappedPrice] = useState<"base" | number | null>(null);

  /** On the selected row (or a tapped one in pickup) an editable price is an open input */
  const priceIsOpen = (target: "base" | number) =>
    priceIsEditable(target) &&
    (mode === "checkin" ? isSelected : tappedPrice === target);

  // The price tapped to select the row gets the caret (else the unit price)
  const tappedToSelectRef = useRef<"base" | number | null>(null);

  /** Tapping a price: selects its row, which opens the price */
  const handlePriceClick = (e: React.MouseEvent, target: "base" | number) => {
    e.stopPropagation();
    if (!priceIsEditable(target)) return;
    if (priceIsOpen(target)) {
      // Tapping an open price again selects all of it, so typing replaces it
      const input = e.currentTarget as HTMLInputElement;
      window.setTimeout(() => input.select(), 30);
      return;
    }
    if (mode === "checkin") {
      tappedToSelectRef.current = target;
      handleOnSelectCartItem();
    } else setTappedPrice(target);
  };

  /** Leaving the box */
  const handlePriceBlur = (target: "base" | number) => {
    submitPrice(target);
    if (pendingPriceRef.current === null && mode !== "checkin") setTappedPrice(null);
  };

  // Selecting the row puts the caret in its unit price, the whole price
  // selected so typing replaces it.
  const focusKey: string | null =
    mode === "checkin" ? (isSelected ? "selected" : null) : tappedPrice === null ? null : String(tappedPrice);
  useEffect(() => {
    if (focusKey === null) return;
    const target =
      mode === "checkin" ? tappedToSelectRef.current ?? "base" : tappedPrice;
    tappedToSelectRef.current = null;
    if (target === null || !priceIsOpen(target)) return;
    const input = document.querySelector<HTMLInputElement>(
      `input[name="${priceInputName(target)}"]`
    );
    if (!input) return;
    input.focus();
    // The number box moves the caret just after focusing; select after that
    const timer = window.setTimeout(() => input.select(), 30);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  const priceChanged = (target: "base" | number) => {
    if (target === "base") return unitPrice !== cartItem.unitPrice;
    const draft = alterationDrafts[target];
    return draft !== undefined && draft !== cartItem.alterationItems[target]?.unitPrice;
  };

  /** Enter or leaving the box: a changed price asks for the manager code */
  const submitPrice = (target: "base" | number) => {
    if (pendingPriceRef.current !== null || !priceChanged(target)) return;
    pendingPriceRef.current = target;
    setCodeModalOpen(true);
  };

  const applyPendingPrice = () => {
    const target = pendingPriceRef.current;
    pendingPriceRef.current = null;
    setTappedPrice(null);
    if (target === null) return;
    const clonedCartItem = cloneDeep(cartItem);
    if (target === "base") {
      clonedCartItem.unitPrice = unitPrice;
      clonedCartItem.isPriceEdited = true;
    } else {
      clonedCartItem.alterationItems[target].unitPrice = alterationDrafts[target];
    }
    dispatch(updateCartSessionItem({ cartItem: clonedCartItem }));
    setAlterationDrafts({});
  };

  /** Manager code closed without approving: the old price comes back */
  const cancelPendingPrice = () => {
    setCodeModalOpen(false);
    pendingPriceRef.current = null;
    setTappedPrice(null);
    setUnitPrice(cartItem.unitPrice);
    setAlterationDrafts({});
  };

  // An open price is drawn as a white, outlined input box
  const priceInputStyles = (open: boolean): React.CSSProperties => ({
    padding: "0",
    paddingInline: "0",
    paddingBlock: "0",
    background: open ? Colors.WHITE : "none",
    border: open ? `0.15rem solid ${Colors.SECONDARY}` : "none",
    borderRadius: "0.3rem",
    cursor: open ? "text" : "pointer",
    height: open ? "2rem" : "1.5rem",
    width: "100%",
    fontSize: "var(--fs-2xs)",
    textAlign: "center",
    outline: "none",
  });

  const removeAlteration = (index: number) => {
    let clonedCartItem = cloneDeep(cartItem);
    if (clonedCartItem) {
      clonedCartItem.alterationItems.splice(index, 1);
      // Alteration Only frees the base price only while there's an alteration to charge for.
      if (clonedCartItem.alterationItems.length === 0) clonedCartItem.alterationOnly = false;
      dispatch(updateCartSessionItem({ cartItem: clonedCartItem }));
    }
  };

  const handleRemoveUpcharge = (upcharge: ISelectedUpcharge) => {
    const cartItem = cloneDeep(selectedCartItem);
    if (cartItem) {
      cartItem.upcharges = cartItem.upcharges.filter(
        (upchargeItem) => upchargeItem.id !== upcharge.id
      );
      //   cartItem.unitPrice -= upcharge.price;
      // price isn't updating when dispatching
      dispatch(updateCartSessionItem({ cartItem }));
    }
  };
  const handleRemoveSpot = (spot: ISelectedSpot) => {
    const cartItem = cloneDeep(selectedCartItem);
    if (cartItem) {
      cartItem.spots = cartItem.spots.filter(
        (spotItem) => spotItem.id !== spot.id
      );
      dispatch(updateCartSessionItem({ cartItem }));
    }
  };

  function splitNote(note: any, firstLineLimit: any, otherLineLimit: any) {
    const result = [];
    let current = 0;

    result.push(note.slice(current, current + firstLineLimit));
    current += firstLineLimit;

    while (current < note.length) {
      result.push(note.slice(current, current + otherLineLimit));
      current += otherLineLimit;
    }

    return result;
  }

  const isPickup = cartMode === "pickup";
  const isRefunded = !!refundStatus;
  const isVoided = !!voidStatus;
  const isPickedUp = !!pickingStatus;
  const isconveyored = !!hasConveyor;

  const shortStatus = (() => {
    if (!isPickup) return "IP";
    if (isRefunded && isVoided && !isconveyored) return "RF,VD";
    if (isRefunded && isVoided && isconveyored) return "RF,VD,RE";
    if (isRefunded && isconveyored) return "RF,RE";
    if (isVoided && isconveyored) return "VD,RE";
    if (isVoided) return "VD";
    if (isRefunded && isPickedUp) return "RF,PU";
    if (isRefunded) return "RF";
    if (isPickedUp) return "PU";
    if (isconveyored && !isPickedUp) return "RE";
    return "IP";
  })();

  const fullStatusMap: Record<string, string> = {
    "RF,VD": "Refunded, Voided",
    "RF,PU": "Refunded, Picked Up",
    "RF,VD,RE": "Refunded, Picked Up, Ready to be picked up",
    "RF,RE": "Refunded, Ready to be picked up",
    "VD,RE": "Picked Up, Ready to be picked up",
    RF: "Refunded",
    VD: "Voided",
    PU: "Picked Up",
    IP: "In Progress",
    RE: "Ready to be picked up",
  };

  const fullStatus = fullStatusMap[shortStatus] ?? null;

  const isCheckin = cartMode === "check-in";

  const checkinAbbrevMap: Record<string, string> = {
    "In Progress": "IP",
    Completed: "CP",
    Cancelled: "CN",
  };

  const displayStatus = isCheckin
    ? checkinAbbrevMap[status] ?? status
    : (shortStatus as string);

  const tooltipLabel =
    isCheckin && status === "In Progress" ? "In Progress" : fullStatus;

  function formatColorText(text: string): string {
    if (!text) return "";

    const parts = text.split("_");
    return parts
      .map((p) => {
        if (p.length === 0) return "";
        return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
      })
      .join("_");
  }

  const dcColorAndTextureText = [
    (cartItem.garmentColor || [])
      .map((color) => formatColorText(color ?? ""))
      .join(", "),
    formatColorText(cartItem.texture || ""),
  ];
  // Editable unit price; for Press Only / Clean Only it sits on that line,
  // under the regular unit price struck through.
  const baseOpen = priceIsOpen("base");
  const unitPriceInput = (
    <CustomTextBox
      name={priceInputName("base")}
      styles={priceInputStyles(baseOpen)}
      onClick={(e) => handlePriceClick(e, "base")}
      onKeyDown={(e: any) => {
        if (!baseOpen && e.key !== "Tab") {
          e.preventDefault();
        } else if (baseOpen && e.key === "Enter") {
          submitPrice("base");
        }
      }}
      inputProps={{
        readOnly: !baseOpen,
      }}
      value={getUnitPrice()}
      type="currency"
      onChange={(e: any) => {
        if (!baseOpen) return;
        handleUnitPriceChange(e);
      }}
      onBlur={() => {
        if (baseOpen) handlePriceBlur("base");
      }}
    />
  );

  // An alteration's unit price, edited the same way as the item's
  const alterationPriceInput = (unitPriceValue: number | undefined, index: number) => {
    const open = priceIsOpen(index);
    return (
      <CustomTextBox
        name={priceInputName(index)}
        styles={{
          ...priceInputStyles(open),
          textDecoration: voidStatus ? "line-through" : "none",
        }}
        onClick={(e) => handlePriceClick(e, index)}
        onKeyDown={(e: any) => {
          if (!open && e.key !== "Tab") {
            e.preventDefault();
          } else if (open && e.key === "Enter") {
            submitPrice(index);
          }
        }}
        inputProps={{
          readOnly: !open,
        }}
        value={
          cartItem.redoDetails
            ? 0
            : alterationDrafts[index] !== undefined
            ? alterationDrafts[index]
            : unitPriceValue ?? 0
        }
        type="currency"
        onChange={(e: any) => {
          if (!open) return;
          const value = parseFloat(e.target.value);
          setAlterationDrafts((drafts) => ({ ...drafts, [index]: isNaN(value) ? 0 : value }));
        }}
        onBlur={() => {
          if (open) handlePriceBlur(index);
        }}
      />
    );
  };

  return (
    <>
      <ItemNoteModal
        open={itemNoteModalOpen}
        onClose={() => setItemNoteModalOpen(false)}
        cartItem={cartItem}
      />
      <Grid
        container
        sx={{
          gap: "0.35rem",
          flexWrap: "nowrap",
          cursor: isItemLocked ? "default" : undefined,
          ...(cartItem.spots.length === 0 && cartMode === "pickup"
            ? { paddingRight: "0.2rem" }
            : { paddingRight: "0.15rem" }),
        }}
        onClick={
          mode === "checkin" && !isItemLocked
            ? handleOnSelectCartItem
            : undefined
        }
      >
        <Grid
          item
          xs={1}
          sx={{
            ...styles.tableBodyCell,
            ...(firstRow ? styles.firstRowCell : {}),
            ...(lastRow ? styles.lastRowCell : {}),
          }}
        >
          <Typography>{cartItem.qty}</Typography>
          <Box
            sx={{
              height: "0.07rem",
              width: "100%",
              background: "rgba(0,0,0,0.07)",
              display: lastRow ? "none" : "block",
              zIndex: 2,
            }}
          ></Box>
        </Grid>
        <Grid
          item
          xs={6.7}
          sx={{
            ...styles.tableBodyCell,
            ...styles.descriptionCell,
            ...(firstRow ? styles.firstRowCell : {}),
            ...(lastRow ? styles.lastRowCell : {}),
            position: "relative",
          }}
        >
          <IconButton
            aria-label="Item note"
            sx={{
              position: "absolute",
              top: "0rem",
              right: "0rem",
              display:
                selectedCartItem?.itemId !== cartItem.itemId
                  ? cartItem.note !== null
                    ? "block"
                    : "none"
                  : "block",
            }}
            onClick={(e) => {
              if (selectedCartItem?.itemId !== cartItem.itemId) {
                dispatch(CartSliceAction.setSelectedCartItem(cartItem));
              }
              setItemNoteModalOpen(true);
              e.preventDefault();
              e.stopPropagation();
            }}
            disabled={
              selectedCartItem?.itemId !== cartItem.itemId
                ? cartItem.note !== null
                  ? false
                  : true
                : false
            }
          >
            <SpeakerNotesIcon
              sx={{
                color:
                  cartItem.note === null ? Colors.MAIN_GRAY : Colors.PRIMARY,
              }}
            />
          </IconButton>
          <Box sx={{ width: "100%" }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  height: 12,
                }}
              >
                <Typography
                  fontWeight="bold"
                  sx={{ textDecoration: isItemPickedUp ? "line-through" : "none" }}
                >
                  {cartItem?.serviceType?.categoryName || ""}
                </Typography>
                {isItemLocked && (
                  <Typography
                    sx={{ ml: 1, fontSize: "0.85em", color: Colors.MAIN_GRAY }}
                  >
                    {lockedLabel}
                  </Typography>
                )}
              </Box>
              <Typography
                sx={{ textDecoration: isItemPickedUp ? "line-through" : "none" }}
              >
                {dcSelectionText.filter((text) => text !== "").join(", ")}
              </Typography>
              <Typography>
                {dcColorAndTextureText.filter((text) => text !== "").join(", ")}
              </Typography>
              {cartItem.alterationItems &&
                cartItem.alterationItems.length > 0 && (
                  <>
                    {cartItem.alterationItems.map((item, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <Box>
                          <Typography>+ </Typography>
                        </Box>
                        <Typography>
                          Alterations -&nbsp;
                          {item.alterationSubCategory?.name || "No Subcategory"}
                        </Typography>
                        <Typography sx={{ ml: 1 }}>
                          {item?.alterationPrice?.name ||
                            "No Second Sub Category"}
                        </Typography>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isItemLocked) return;
                            removeAlteration(index);
                          }}
                          disabled={isItemLocked}
                          sx={{ ml: 1 }}
                        >
                          <CrossIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </>
                )}
              {cartItem.upcharges && cartItem.upcharges.length > 0 && (
                <>
                  {cartItem.upcharges.map((upcharge, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", mt: 1 }}
                    >
                      <Box>
                        <Typography>+ </Typography>
                      </Box>
                      <Typography>
                        Upcharges -&nbsp;{upcharge.label} (
                        {upcharge.subUpcharge.upchargeVariant})
                      </Typography>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isItemLocked) return;
                          handleRemoveUpcharge(upcharge);
                        }}
                        disabled={isItemLocked}
                        sx={{ ml: 1 }}
                      >
                        <CrossIcon />
                      </IconButton>
                    </Box>
                  ))}
                </>
              )}
              {cartItem.spots && cartItem.spots.length > 0 && (
                <>
                  {cartItem.spots.map((spot, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", mt: 1 }}
                    >
                      <Box>
                        <Typography>+ </Typography>
                      </Box>
                      <Typography>
                        Spots -&nbsp;{spot.label}{" "}
                        {spot.subSpot.comment
                          ? `(${spot.subSpot.comment})`
                          : ""}
                      </Typography>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isItemLocked) return;
                          handleRemoveSpot(spot);
                        }}
                        disabled={isItemLocked}
                        sx={{ ml: 1 }}
                      >
                        <CrossIcon />
                      </IconButton>
                    </Box>
                  ))}
                </>
              )}
              {specialCharge > 0 && (
                <Box
                  test-name="cart-row-special-charge"
                  sx={{ display: "flex", alignItems: "center", mt: 1, minHeight: 40 }}
                >
                  <Typography>+ Special Service</Typography>
                </Box>
              )}
              {cartItem.pressOnly && (
                <OrderCartItemProcessType
                  label="Press Only"
                  onRemove={() => {
                    if (isItemLocked) return;
                    const clonedCartItem = cloneDeep(cartItem);
                    clonedCartItem.pressOnly = false;
                    dispatch(updateCartSessionItem({ cartItem: clonedCartItem }));
                  }}
                />
              )}
              {cartItem.cleanOnly && (
                <OrderCartItemProcessType
                  label="Clean Only"
                  onRemove={() => {
                    if (isItemLocked) return;
                    const clonedCartItem = cloneDeep(cartItem);
                    clonedCartItem.cleanOnly = false;
                    dispatch(updateCartSessionItem({ cartItem: clonedCartItem }));
                  }}
                />
              )}
              {cartItem.alterationOnly && (
                <OrderCartItemProcessType
                  label="Alteration Only"
                  onRemove={() => {
                    if (isItemLocked) return;
                    const clonedCartItem = cloneDeep(cartItem);
                    clonedCartItem.alterationOnly = false;
                    dispatch(updateCartSessionItem({ cartItem: clonedCartItem }));
                  }}
                />
              )}
              {cartItem.redoDetails && (
                <OrderCartItemProcessType
                  label={`Redo: ${cartItem.redoDetails.reason || 'Requested'}`}
                  onRemove={() => {
                    if (isItemLocked) return;
                    const clonedCartItem = cloneDeep(cartItem);
                    clonedCartItem.redoDetails = null;
                    dispatch(updateCartSessionItem({ cartItem: clonedCartItem }));
                  }}
                />
              )}
            </Box>
            {cartItem.instructions.length > 0 && (
              <Box sx={{ display: "flex" }}>
                <Box>
                  <Typography>+ </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ fontWeight: 500 }}>
                    Instructions -&nbsp;
                    {cartItem.instructions
                      .map((instruction, i) => {
                        return upperFirst(
                          (instruction?.instruction ?? "").toLowerCase()
                        );
                      })
                      .join(", ")}
                  </Typography>
                </Box>
              </Box>
            )}
            {cartItem.damages.length > 0 && (
              <Box sx={{ display: "flex" }}>
                <Box>
                  <Typography>+ </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ fontWeight: 500 }}>
                    Damages -&nbsp;
                    {cartItem.damages
                      .map((damage, i) => {
                        return upperFirst((damage?.damage ?? "").toLowerCase());
                      })
                      .join(", ")}
                  </Typography>
                </Box>
              </Box>
            )}
            {(cartItem.releases?.length ?? 0) > 0 && (
              <Box sx={{ display: "flex" }}>
                <Box>
                  <Typography>+ </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ fontWeight: 500 }}>
                    Releases -&nbsp;
                    {(cartItem.releases ?? []).map((release) => release.name).join(", ")}
                  </Typography>
                </Box>
              </Box>
            )}
            {cartItem.note && (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {splitNote(
                  upperFirst((cartItem?.note ?? "").toLowerCase()),
                  40,
                  48
                ).map((line, index) => (
                  <Box key={index} sx={{ display: "flex" }}>
                    <Box>
                      <Typography>{index === 0 ? "+ " : "  "}</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {index === 0 ? "Note - " : ""}
                        {line}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              height: "0.07rem",
              width: "100%",
              background: "rgba(0,0,0,0.07)",
              display: lastRow ? "none" : "block",
              zIndex: 2,
            }}
          ></Box>
        </Grid>
        <Grid
          item
          xs={1.7}
          sx={{
            ...styles.tableBodyCell,
            ...(firstRow ? styles.firstRowCell : {}),
            ...(lastRow ? styles.lastRowCell : {}),
          }}
        >
          {tooltipLabel ? (
            <Tooltip
              title={
                <Typography style={{ fontSize: "1rem" }}>
                  {tooltipLabel}
                </Typography>
              }
              placement="top"
              TransitionComponent={Zoom}
              leaveDelay={100}
              disableFocusListener
              arrow
            >
              <Box sx={{ width: "100%" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ textAlign: "center" }}>
                    {displayStatus}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          ) : (
            <Box
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
            >
              <Typography sx={{ textAlign: "center" }}>
                {displayStatus}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              height: "0.07rem",
              width: "100%",
              background: "rgba(0,0,0,0.07)",
              display: lastRow ? "none" : "block",
              zIndex: 2,
            }}
          />
        </Grid>

        <Grid
          item
          xs={1.3}
          sx={{
            ...styles.tableBodyCell,
            ...(firstRow ? styles.firstRowCell : {}),
            ...(lastRow ? styles.lastRowCell : {}),
          }}
        >
          {processingBasePrice !== null ? (
            <Typography
              test-name="cart-row-base-unit-price"
              sx={{ textDecoration: "line-through", opacity: 0.55 }}
            >
              {formatCurrency({ amount: processingBasePrice })}
            </Typography>
          ) : (
            unitPriceInput
          )}
          {cartItem.alterationItems.map((item, index) => (
            <Box sx={{ mt: "0.571428571rem" }} key={index}>
              {alterationPriceInput(item.unitPrice, index)}
            </Box>
          ))}
          {cartItem.upcharges.map((upcharge, index) => (
            <Box sx={{ mt: "0.571428571rem" }} key={index}>
              <Typography>
                {formatCurrency({
                  amount: cartItem.redoDetails ? 0 : upcharge.subUpcharge.price ?? 0,
                })}
              </Typography>
            </Box>
          ))}
          {cartItem.spots.map((spot, index) => (
            <Box sx={{ mt: "0.571428571rem" }} key={index}>
              <Typography>
                {formatCurrency({
                  amount: cartItem.redoDetails ? 0 : spot.subSpot.price ?? 0,
                })}
              </Typography>
            </Box>
          ))}
          {specialCharge > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 1, minHeight: 40 }}>
              <Typography test-name="cart-row-special-unit-price">
                {formatCurrency({ amount: specialCharge / Math.max(1, cartItem.qty) })}
              </Typography>
            </Box>
          )}
          {processingBasePrice !== null && (
            // Reduced unit price, on the Press Only / Clean Only line.
            <Box sx={{ display: "flex", alignItems: "center", mt: 1, minHeight: 40 }}>
              {unitPriceInput}
            </Box>
          )}
          {cartItem.instructions.length > 0 && (
            <Box sx={{ display: "flex" }}>&nbsp;</Box>
          )}
          {cartItem.damages.length > 0 && (
            <Box sx={{ display: "flex" }}>&nbsp;</Box>
          )}
          <Box
            sx={{
              height: "0.07rem",
              width: "100%",
              background: "rgba(0,0,0,0.07)",
              display: lastRow ? "none" : "block",
              zIndex: 2,
            }}
          ></Box>
        </Grid>
        <Grid
          item
          xs={1.3}
          sx={{
            ...styles.tableBodyCell,
            ...(firstRow ? styles.firstRowCell : {}),
            ...(lastRow ? styles.lastRowCell : {}),
          }}
        >
          {processingBasePrice !== null ? (
            <Typography
              test-name="cart-row-base-price"
              sx={{ textDecoration: "line-through", opacity: 0.55 }}
            >
              {formatCurrency({ amount: cartItem.qty * processingBasePrice })}
            </Typography>
          ) : (
            <Typography>{getPrice()}</Typography>
          )}
          {cartItem.alterationItems.map((item, index) => (
            <Box sx={{ mt: "0.571428571rem" }} key={index}>
              <Typography>
                {formatCurrency({
                  amount: cartItem.redoDetails ? 0 : cartItem.qty * (item.unitPrice || 0),
                })}
              </Typography>
            </Box>
          ))}
          {cartItem.upcharges.map((upcharge, index) => (
            <Box sx={{ mt: "0.571428571rem" }} key={index}>
              <Typography>
                {formatCurrency({
                  amount: cartItem.redoDetails
                    ? 0
                    : upcharge.subUpcharge.price
                      ? cartItem.qty * upcharge.subUpcharge.price
                      : 0,
                })}
              </Typography>
            </Box>
          ))}
          {cartItem.spots.map((spot, index) => (
            <Box sx={{ mt: "0.571428571rem" }} key={index}>
              <Typography>
                {formatCurrency({
                  amount: cartItem.redoDetails
                    ? 0
                    : spot.subSpot.price
                      ? cartItem.qty * spot.subSpot.price
                      : 0,
                })}
              </Typography>
            </Box>
          ))}
          {specialCharge > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1, minHeight: 40 }}>
              <Typography test-name="cart-row-special-total">
                {formatCurrency({ amount: specialCharge })}
              </Typography>
            </Box>
          )}
          {processingBasePrice !== null && (
            // Lines up with the Press Only / Clean Only row (its remove
            // button sets the row height).
            <Box sx={{ display: "flex", alignItems: "center", mt: 1, minHeight: 40 }}>
              <Typography test-name="cart-row-processing-price">{getPrice()}</Typography>
            </Box>
          )}
          {cartItem.instructions.length > 0 && (
            <Box sx={{ display: "flex" }}>&nbsp;</Box>
          )}
          {cartItem.damages.length > 0 && (
            <Box sx={{ display: "flex" }}>&nbsp;</Box>
          )}
          <Box
            sx={{
              height: "0.07rem",
              width: "100%",
              background: "rgba(0,0,0,0.07)",
              display: lastRow ? "none" : "block",
              zIndex: 2,
            }}
          ></Box>
          <ManagerCodeModal
            open={codeModalOpen}
            onClose={cancelPendingPrice}
            title="Manager Code"
            task="edit the unit price"
            onDone={handleManagerAuthSuccess}
            actionType="EDIT_UNIT_PRICE"
            requestedByUserId={user?.id}
            billId={billById?.id}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default CartRow;
